import { LocalStorageService } from './local-storage.service';
import { CacheService } from './cache.service';
import { TenantContextService } from './tenant-context.service';
import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Observable, tap, map, of, catchError, switchMap } from 'rxjs';
import {
  LoginDialogComponent,
  LoginDialogData,
  LoginDialogMode,
  LoginDialogResult,
} from '../components/login-dialog/login-dialog.component';

interface LoginResponse { token: string; expiresAt: string; }
export interface AccountIdentity { companyName: string; userName: string; }

const TOKEN_KEY = 'br_token';

@Injectable({ providedIn: 'root' })
export class AuthService implements OnDestroy {
  private sessionToken: string | null | undefined;
  private readonly onStorage = (event: StorageEvent) => {
    if (event.key !== TOKEN_KEY && event.key !== null) return;
    this.getToken();
    this.loadPermissions().subscribe();
  };

  constructor(
    private http: HttpClient,
    private dialog: MatDialog,
    private router: Router,
    private readonly cache: CacheService,
    readonly tenant: TenantContextService,
    private readonly storage: LocalStorageService,
  ) {
    window.addEventListener('storage', this.onStorage);
  }

  ngOnDestroy(): void {
    window.removeEventListener('storage', this.onStorage);
  }

  login(companyName: string, identifier: string, password: string): Observable<void> {
    return this.http.post<LoginResponse>('/api/auth/login', { companyName, identifier, password }).pipe(
      tap(r => { this.storeToken(r.token); this.getToken(); }),
      switchMap(() => this.fetchPermissions())
    );
  }

  // Temporarily disabled: POST /api/auth/signup is commented out in
  // AuthController, so this asked for a route nobody served and the dialog
  // reported the 404 as if the credentials were wrong. Restore both together.
  // register(email: string, password: string, companyName: string): Observable<void> {
  //   return this.http.post<LoginResponse>('/api/auth/signup', { email, password, companyName }).pipe(
  //     tap(r => { this.storeToken(r.token); this.getToken(); }),
  //     switchMap(() => this.fetchPermissions())
  //   );
  // }

  logout(): Observable<void> {
    const token = this.getToken();
    const clear = () => { if (this.getToken() === token) this.clearSession(); };
    return this.http.post<void>('/api/auth/logout', {}).pipe(
      tap(clear),
      catchError(() => { clear(); return of(undefined); })
    );
  }

  forgotPassword(companyName: string, email: string): Observable<void> {
    return this.http.post<void>('/api/auth/forgot-password', { companyName, email });
  }

  resetPassword(token: string, newPassword: string): Observable<void> {
    return this.http.post<void>('/api/auth/reset-password', { token, newPassword });
  }

  changePassword(currentPassword: string, newPassword: string): Observable<void> {
    return this.http.post<void>('/api/auth/change-password', { currentPassword, newPassword });
  }

  private storeToken(token: string): void {
    if (!this.storage.setItem(TOKEN_KEY, token, 'browser')) {
      throw new Error('Unable to save your sign-in in this browser.');
    }
  }

  getToken(): string | null {
    const token = this.storage.getItem(TOKEN_KEY, 'browser');
    if (token !== this.sessionToken) {
      this.sessionToken = token;
      const claims = this.decodeClaims(token);
      if (claims) {
        this.tenant.startSession({
          companyId: claims['companyId'],
          userId: claims['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier']
            ?? claims['nameid'] ?? claims['sub'] ?? claims['userId'],
          isPlatformStaff: claims['isPlatformStaff'],
        });
      } else this.tenant.endSession();
    }
    return token;
  }

  /** Decodes the JWT payload (claims) without verifying the signature. */
  getClaims(): Record<string, unknown> | null {
    return this.decodeClaims(this.getToken());
  }

  private decodeClaims(token: string | null): Record<string, unknown> | null {
    if (!token) return null;
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    try {
      const json = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
      return JSON.parse(decodeURIComponent(escape(json)));
    } catch {
      return null;
    }
  }

  getEmail(): string | null {
    const email = this.getClaims()?.['email'];
    return typeof email === 'string' && email.length > 0 ? email : null;
  }

  getAccountIdentity(): Observable<AccountIdentity> {
    return this.http.get<AccountIdentity>('/api/auth/account');
  }

  isAuthenticated(): boolean {
    // Presence AND validity: a present-but-expired token must read as
    // unauthenticated so the shell shows the landing page (no paid map tiles)
    // instead of briefly mounting the map until the first 401. JWT `exp` is
    // seconds since epoch; a token without `exp` is treated as valid.
    const claims = this.getClaims();
    if (!claims || !this.tenant.current || !this.cache.has('auth', 'permissions')) return false;
    const exp = claims['exp'];
    return typeof exp === 'number' ? exp * 1000 > Date.now() : true;
  }

  hasPermission(key: string): boolean {
    // Resolves against the cached permission set loaded from
    // GET /api/auth/permissions (the union of the user's role grants).
    // Returns false until the set is loaded, so callers must ensure
    // loadPermissions() has completed (APP_INITIALIZER + login both do).
    this.getToken();
    return this.cache.get<Set<string>>('auth', 'permissions')?.has(key) ?? false;
  }

  /**
   * Ensures the permission set is loaded once. Used at app startup to rehydrate
   * after a page refresh (token survives in localStorage, the in-memory cache
   * does not). Login starts a new cache scope before loading its permissions.
   */
  loadPermissions(): Observable<void> {
    if (!this.getToken() || !this.tenant.current) return of(undefined);
    return this.cache.has('auth', 'permissions') ? of(undefined) : this.fetchPermissions();
  }

  /** Fetches the user's effective permission keys and replaces the cache. */
  private fetchPermissions(): Observable<void> {
    return this.cache.load('auth', 'permissions', () =>
      this.http.get<string[]>('/api/auth/permissions').pipe(
        map(keys => new Set(keys)),
        catchError(() => of(new Set<string>())),
      ),
    ).pipe(map(() => undefined));
  }

  invalidatePermissionCache(): void {
    this.cache.clear('auth');
  }

  /**
   * Called by the 401 interceptor when an authenticated request is rejected —
   * i.e. the JWT expired or was revoked mid-session (the token is 24h with no
   * refresh). Clears the stale session and prompts re-login, returning the user
   * to where they were. No-op if there is no token (nothing to expire, or a
   * concurrent 401 already handled it), which also prevents a prompt loop.
   */
  handleSessionExpired(): void {
    if (!this.getToken()) return;
    this.clearSession();
    this.openLoginDialog(this.router.url);
  }

  openLoginDialog(returnUrl?: string, mode?: LoginDialogMode): void {
    if (this.dialog.openDialogs.some(d => d.componentInstance instanceof LoginDialogComponent)) {
      return;
    }
    const ref = this.dialog.open<LoginDialogComponent, LoginDialogData, LoginDialogResult>(
      LoginDialogComponent,
      {
        width: '440px',
        maxWidth: '90vw',
        panelClass: 'login-dialog-panel',
        backdropClass: 'gpt-style-backdrop',
        data: { returnUrl, mode },
        autoFocus: 'first-tabbable',
      }
    );
    ref.afterClosed().subscribe(result => {
      if (result?.ok && result.returnUrl) {
        this.router.navigateByUrl(result.returnUrl);
      }
    });
  }

  private clearSession(): void {
    if (!this.storage.removeItem(TOKEN_KEY, 'browser')) {
      throw new Error('Unable to clear your sign-in in this browser.');
    }
    this.getToken();
  }
}
